// Create Competition screen
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Card } from '../components';
import { createCompetition, createPlayer, isAnonymous } from '../services';
import { createPlayerToken } from '../services/tokenService';
import { storeToken } from '../utils/localStorage';
import { generatePin } from '../utils/crypto';
import { getTodayDateString } from '../utils/dateHelpers';

const CreateCompetition = () => {
  const navigate = useNavigate();
  const { authUid, user } = useAuth();

  // Check if user is anonymous and redirect to account creation
  useEffect(() => {
    if (isAnonymous()) {
      navigate('/create-account', {
        state: {
          returnTo: '/create',
          requireAccount: true
        }
      });
    }
  }, [navigate]);

  // Helper to add days to a date string
  const addDaysToDate = (dateString, days) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(getTodayDateString());
  const [endDate, setEndDate] = useState(addDaysToDate(getTodayDateString(), 4));
  const [maxPlayers, setMaxPlayers] = useState(20);
  const [rankingMethod, setRankingMethod] = useState('byCategory');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [generatedPin, setGeneratedPin] = useState('');
  const [competitionId, setCompetitionId] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate dates
      if (endDate && startDate > endDate) {
        setError('End date must be after start date');
        setLoading(false);
        return;
      }

      // Create organizer player first (with a temporary competition ID)
      const pin = generatePin(6);

      // We'll create the player after competition, but we need to generate IDs first
      const { v4: uuidv4 } = await import('uuid');
      const playerId = uuidv4();

      // Create competition with the player ID
      const competition = await createCompetition(
        {
          name,
          startDate,
          endDate: endDate || startDate,
          maxPlayers: parseInt(maxPlayers),
          rankingMethod,
          timezone: 'Europe/Oslo'
        },
        playerId // Set organizerId to the player we're about to create
      );

      // Now create the player with the pre-generated playerId and link to auth account
      const player = await createPlayer(competition.id, displayName, pin, playerId, authUid);

      // Create token
      const tokenId = await createPlayerToken(competition.id, player.id, authUid);
      storeToken(competition.id, tokenId);

      setGeneratedPin(pin);
      setCompetitionId(competition.id);

      // If user has an account (not anonymous), skip PIN screen and go directly to competition
      if (user?.email) {
        navigate(`/competition/${competition.id}`);
      } else {
        setShowPin(true);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error creating competition:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate(`/competition/${competitionId}`);
  };

  if (showPin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full">
          <Card>
            <h2 className="text-2xl font-bold mb-4 text-center">Competition Created!</h2>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <p className="text-sm text-gray-700 mb-2 text-center">Your PIN:</p>
              <p className="text-3xl font-bold text-center text-gray-900 tracking-widest">{generatedPin}</p>
            </div>
            <p className="text-sm text-red-600 mb-4 text-center font-semibold">
              Save this PIN! You'll need it to access this competition from other devices.
            </p>
            <Button onClick={handleContinue} fullWidth>
              Continue to Competition
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create Competition</h1>
          <p className="mt-2 text-gray-600">Set up a new multi-day competition</p>
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            <Input
              label="Competition Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Summer Games 2025"
              required
            />

            <Input
              label="Your Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              required
            />

            <Input
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setStartDate(newStartDate);
                setEndDate(addDaysToDate(newStartDate, 4));
              }}
              required
            />

            <Input
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />

            <Input
              label="Max Players"
              type="number"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(e.target.value)}
              min="2"
              max="100"
              required
            />

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ranking Method
              </label>
              <select
                value={rankingMethod}
                onChange={(e) => setRankingMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="byCategory">By Category</option>
                <option value="byDay">By Day</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {rankingMethod === 'byCategory'
                  ? 'Players ranked by total placement points across all categories'
                  : 'Players ranked by daily placement points, calculated from category performance each day'}
              </p>
            </div>

            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Creating...' : 'Create Competition'}
            </Button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full mt-3 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default CreateCompetition;
